import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

import { AuthService } from "src/app/service/auth.service";
import { MonitoringService } from "src/app/service/monitoring.service";

/** "nothingToSummarize" is a successful answer: the email held no content to send to the model. */
export type AiSummaryOutcome = "summarized" | "nothingToSummarize";

export interface AiThreadSummaryResponse {
  outcome: AiSummaryOutcome;
  subject: string;
  narrative: string;
  participants: string[];
}

export const aiSummaryErrorCodes = ["notLicensed", "noMailboxPermission", "generationFailed", "featureDisabled", "aiNotEnabled"] as const;

export type AiSummaryErrorCode = typeof aiSummaryErrorCodes[number];

/** A summarize call that came back with a reason the caller can act on, rather than a raw HTTP failure. */
export class AiSummaryError extends Error {
  constructor(readonly code: AiSummaryErrorCode) {
    super(`AI summary failed: ${code}`);
    this.name = "AiSummaryError";
  }
}

// The AI-summary data access that does NOT need NAA (Graph delegated-token) auth. summarizeThread
// does need it and stays on SoApiService for now: giving it a home here would also mean moving the
// shared NAA claim-challenge retry plumbing that other, unrelated features still depend on there.
@Injectable({
  providedIn: "root",
})
export class AiSummaryApiService {
  private entitlement: boolean | undefined;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly monitoringService: MonitoringService,
    private readonly authService: AuthService,
    @Inject("BASE_URL") private readonly baseUrl: string
  ) { }

  // Cached for the session: a licence or CRM toggle change reaches the offer on the next add-in
  // load, while the server checks again on every summarize.
  // Failures aren't cached (lets a transient error retry); a 404 means the Feature was disabled.
  async getEntitlement(): Promise<boolean> {
    if (this.entitlement !== undefined) {
      return this.entitlement;
    }

    try {
      const response = await firstValueFrom(
        this.httpClient.get<{ isEntitled: boolean }>(`${this.baseUrl}api/Ai/summaryEntitlement`));
      this.entitlement = response?.isEntitled ?? false;
      return this.entitlement;
    } catch (e) {
      if ((e as HttpErrorResponse)?.status === 404) {
        throw new AiSummaryError("featureDisabled");
      }

      this.monitoringService.logFailed("FAILED: get AI summary entitlement", e);
      return false;
    }
  }

  // Re-resolves enabledFeatures without a full re-login, updating UserInfoService so
  // hasFeature-bound bindings pick it up mid-session.
  async refreshEnabledFeatures(): Promise<void> {
    await this.authService.isEnrolled();
  }
}
